import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { asyncScheduler, Observable, scheduled } from 'rxjs';

import { ErrorService } from '../../../../../shared/services/error-service';
import { ToastService } from '../../../../../shared/toast/toast.service';
import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';

import { RenewalClaimRangeMappingInput, RenewalClaimService,WorkbookService } from '../../../../../../service/proxy';

@Component({
    standalone: true,
    selector: 'claim-data-range-selection',
    templateUrl: './claim-data-range-selection.component.html',
    styleUrl: './claim-data-range-selection.component.scss',

    imports: [
        CommonModule,
        FormsModule,
    ],

    providers: [
        RenewalClaimService,
        WorkbookService
    ]
})
export class ClaimDataRangeSelectionComponent implements WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;
    public firstTenRows: any[] = [];
    public lastTenRows: any[] = [];
    public errorMessage: string = '';
    public loading: boolean = false;
    public isWideTable = false;
    public model: RenewalClaimRangeMappingInput = new RenewalClaimRangeMappingInput();

    constructor(
        private route: ActivatedRoute,
        private workbookService: WorkbookService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private renewalClaimService: RenewalClaimService,
    ) {}

    ngOnInit(): void {
        // Leave empty
    }

    onActivate(): void {
        this.route.paramMap.subscribe(params => {
            this.batchId = Number(params.get('id'));

            if (this.batchId > 0) {
                // Always reload the saved range values from the database
                this.getImportRowMapping();

                // Only fetch the file preview on first activation.
                // On back-navigation the rows are already in memory — no need to hit the server again.
                if (this.firstTenRows.length === 0) {
                    this.getBatch();
                }
            }
        });
    }

    getBatch() {
        this.renewalClaimService.getRenewalClaimBatch(this.batchId)
            .subscribe((result) => {
                this.getWorksheetSample(`${result.filePath}//${result.storedName}`);
            }
        );
    }

    getWorksheetSample(path: string): void {
        this.loading = true;
        this.isWideTable = false;

        this.workbookService.getSampleBySheetIndex(path, 0)
            .subscribe({
                next: (result) => {
                    this.firstTenRows = result.firstTenRows || [];
                    this.lastTenRows = result.lastTenRows || [];

                    const headerRow = this.firstTenRows[0];
                    const colCount = headerRow?.rowValues?.length || 0;
                    this.isWideTable = colCount > 12;

                    const lastRowMax = this.lastTenRows.reduce((max, row) => {
                        return row.rowNumber > max ? row.rowNumber : max;
                    }, 0);

                    // Only set endIndex from sample if no saved value exists
                    if (!this.model.endIndex) {
                        this.model.endIndex = lastRowMax;
                    }
                    this.errorMessage = '';
                },
                error: (error) => {
                    this.loading = false;
                    if (error.status === 404) {
                        const errorBody = error.response ? JSON.parse(error.response) : null;
                        if (errorBody?.code === 'FILE_NOT_FOUND') {
                            this.errorMessage = 'Excel file not found. Delete this import batch and upload again.';
                            this.toastService.show(this.errorMessage, 'warning');
                        }
                        else {
                            this.errorMessage = 'Failed to load preview.';
                            this.toastService.show('Could not preview worksheet', 'error');
                        }
                    }
                    else {
                        this.errorMessage = 'Failed to load preview.';
                        this.toastService.show('Could not preview worksheet', 'error');
                    }
                },
                complete: () => {
                    this.loading = false;
                }
            }
        );
    }

    getImportRowMapping(): void {
        this.renewalClaimService.getRenewalClaimRangeMapping(this.batchId)
            .subscribe((result) => {
                if (result) {
                    this.model = new RenewalClaimRangeMappingInput({
                        headerIndex: result.headerIndex || 1,
                        startIndex: result.startIndex || 2,
                        endIndex: result['endIndex'],
                    });
                }
            }
        );
    }

    isValid(): boolean {
        return this.model.headerIndex > -1 && this.model.startIndex > -1 && this.model.endIndex > -1;
    }

    onNext(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            this.renewalClaimService.createRenewalClaimImportRangeMapping(this.batchId, this.model)
                .subscribe({
                    next: () => {
                        this.toastService.show('Renewal claim row mapping saved successfully', 'Success');

                        const mappingsChangedKey = `renewal_claim_mappings_changed_${this.batchId}`;
                        sessionStorage.setItem(mappingsChangedKey, 'true');

                        observer.next(true);
                        observer.complete();
                    },
                    error: (error: any) => {
                        if (error.status === 400) {
                            this.toastService.show('Bad request. Please try again.', 'Info');
                        }
                        else {
                            this.errorService.handleError(error);
                        }

                        observer.next(false);
                        observer.complete();
                    },
                });
        });
    }

    onPrevious(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }
}
